import { t } from "../i18n/t";

/**
 * §B7.1: one `<h1>` per screen, inside `<main>`. §B7.4: a polite live region every screen can announce into
 * ("mapping complete, 12 items to review") without inventing its own.
 *
 * The redesign's page header (guide §3) is eyebrow → h1 → caption with the primary action bottom-right. A
 * `breadcrumb` (a back link, or a breadcrumb nav) stacks the header vertically, above the title row.
 */
export function Page({
  title,
  eyebrow,
  caption,
  announce,
  actions,
  breadcrumb,
  headerBottom,
  titleAccessory,
  titlePrefix,
  narrow = false,
  children,
}: {
  title: string;
  eyebrow?: string;
  /** The muted 14 px line under the h1 (guide §3 page header). */
  caption?: string;
  announce?: string;
  actions?: React.ReactNode;
  /** A back link or breadcrumb nav rendered above the title row (AddConnection, the migration screens). */
  breadcrumb?: React.ReactNode;
  /** Rendered inside a breadcrumb header, under the title row (the wizard rail on the new-migration screens). */
  headerBottom?: React.ReactNode;
  /** Sits inline after the h1 (the state pill on the migration screens). */
  titleAccessory?: React.ReactNode;
  /** Sits beside the title group (the 48 px practice tile on the practice detail header). */
  titlePrefix?: React.ReactNode;
  /** Anonymous, form-shaped screens (sign-in, register, invite): a narrow column centred in the viewport. */
  narrow?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const titleGroup =
    titlePrefix === undefined ? (
      <div className="page__title-group">
        {eyebrow === undefined ? null : <p className="eyebrow">{eyebrow}</p>}
        {titleAccessory === undefined ? (
          <h1>{title}</h1>
        ) : (
          <div className="page__title-row">
            <h1>{title}</h1>
            {titleAccessory}
          </div>
        )}
        {caption === undefined ? null : <p className="page__caption">{caption}</p>}
      </div>
    ) : (
      <div className="page__title-group page__title-group--row">
        {titlePrefix}
        <div className="page__title-group">
          {eyebrow === undefined ? null : <p className="eyebrow">{eyebrow}</p>}
          {titleAccessory === undefined ? (
            <h1>{title}</h1>
          ) : (
            <div className="page__title-row">
              <h1>{title}</h1>
              {titleAccessory}
            </div>
          )}
          {caption === undefined ? null : <p className="page__caption">{caption}</p>}
        </div>
      </div>
    );
  return (
    <main className={narrow ? "page page--narrow" : "page"} id="main">
      {breadcrumb === undefined ? (
        <header className="page__header">
          {titleGroup}
          {actions === undefined ? null : <div className="row">{actions}</div>}
        </header>
      ) : (
        <header className="page__header page__header--column">
          {breadcrumb}
          <div className="page__header-main">
            {titleGroup}
            {actions === undefined ? null : <div className="row">{actions}</div>}
          </div>
          {headerBottom ?? null}
        </header>
      )}
      <div className="visually-hidden" role="status" aria-live="polite" aria-label={t("a11y.live_region")}>
        {announce ?? ""}
      </div>
      {children}
    </main>
  );
}
